import React, { useEffect, useMemo, useState } from "react";
import { Button } from "src/components/ui/Button";
import { LeftArrowWhite } from "src/assets/images/svg/LeftArrow";
import { Controller, useForm } from "react-hook-form";
import Select from "react-select";
import Span from "src/components/ui/Span";
import Preloader from "src/components/ui/Preloader";
import logger from "src/utils/logger";
import { customStyles } from "src/statics/reactSelectCustomStyle";
import { useTranslation } from "react-i18next";
import { getTranslatedCountries } from "src/constants/countryTranslations";

const SelectCountryForm = ({
  idType = "kyc",
  onBack,
  onSubmit,
  onChange,
  initialCountryCode = null,
  compact = false,
  selectedCountries = null,
}) => {
  const { t, i18n } = useTranslation();
  const currentLanguage = i18n.language || "en";
  const {
    control,
    handleSubmit,
    setValue,
    formState: { errors },
  } = useForm({
    defaultValues: {
      country: null,
    },
  });
  const [dropdownOptions, setDropdownOptions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);

  const normalizedInitialCountry = useMemo(() => {
    if (!initialCountryCode) {
      return null;
    }
    return initialCountryCode.toUpperCase();
  }, [initialCountryCode]);

  useEffect(() => {
    const fetchCountries = async () => {
      try {
        setLoading(true);
        let countries = getTranslatedCountries(currentLanguage);
        
        if (selectedCountries && Array.isArray(selectedCountries) && selectedCountries.length > 0) {
          const normalizedSelectedCountries = selectedCountries.map(code => code.toUpperCase());
          countries = countries.filter(country => 
            normalizedSelectedCountries.includes(country.value.toUpperCase())
          );
        }
        
        setDropdownOptions(countries);
      } catch (error) {
        logger.error("Error fetching countries:", error);
      } finally {
        setLoading(false);
      }
    };

    fetchCountries();
  }, [currentLanguage, selectedCountries]);

  useEffect(() => {
    setValue("country", null);
  }, [i18n.language, setValue]);

  useEffect(() => {
    if (!normalizedInitialCountry || dropdownOptions.length === 0) {
      return;
    }

    const matchedOption = dropdownOptions.find(
      (option) => option.value.toUpperCase() === normalizedInitialCountry
    );

    if (matchedOption) {
      setValue("country", matchedOption);
    }
  }, [dropdownOptions, normalizedInitialCountry, setValue]);

  const handleFormSubmit = async (data) => {
    if (!onSubmit) {
      return;
    }

    const selected = data?.country || null;
    if (!selected) {
      return;
    }

    try {
      setSubmitting(true);
      await onSubmit(selected);
    } catch (error) {
      logger.error("Error handling country submit:", error);
    } finally {
      setSubmitting(false);
    }
  };

  const showBackButton = typeof onBack === "function" && !compact;
  const showHeader = !compact;
  const title =
    idType === "kyb" ? t("selectCountry.titleKyb") : t("selectCountry.titleKyc");
  const subtitle =
    idType === "kyb"
      ? t("selectCountry.subtitleKyb")
      : t("selectCountry.subtitleKyc");

  const containerClassName = compact
    ? "w-full"
    : "flex justify-between flex-col items-center h-full w-full";

  return (
    <div className={containerClassName}>
      <div className="w-full">
        {showBackButton && (
          <div className="text-left">
            <Button className="" variant="primaryBack" onClick={onBack}>
              <LeftArrowWhite />
            </Button>
          </div>
        )}
        {showHeader && (
          <div className="grid gap-[16px] mt-10">
            <div className="text-h5 font-bold text-flow-heading">{title}</div>
            <div className="text-body text-Gray900">{subtitle}</div>
          </div>
        )}
        <form
          onSubmit={handleSubmit(handleFormSubmit)}
          className={`flex justify-center flex-col items-start ${showHeader ? "" : "mt-4"
            }`}
        >
          <div className="mt-10 w-full">
            <Controller
              name="country"
              control={control}
              rules={{ required: true }}
              render={({ field }) => (
                <Select
                  {...field}
                  value={field.value}
                  className="w-full text-start"
                  styles={customStyles(errors["country"])}
                  options={dropdownOptions}
                  placeholder={
                    loading
                      ? t("selectCountry.loading")
                      : t("selectCountry.placeholder")
                  }
                  isClearable={true}
                  isLoading={loading}
                  loadingMessage={() => t("selectCountry.loading")}
                  onChange={(selectedOption) => {
                    field.onChange(selectedOption);
                    if (onChange) {
                      onChange(selectedOption);
                    }
                  }}
                />
              )}
            />
            {errors["country"] && (
              <Span className="text-FailRed400 text-xxs">
                {errors["country"].message
                  ? errors["country"].message
                  : t("selectCountry.required")}
              </Span>
            )}
          </div>
          <div className="mt-6 w-full">
            <Button
              type="submit"
              className="mt-4"
              variant="primaryWithoutIcon"
              disabled={submitting}
            >
              <span className="me-1">{t("actions.continue")}</span>
              {submitting && <Preloader />}
            </Button>
          </div>
        </form>
      </div>
      {!compact && <div className="" />}
    </div>
  );
};

export default SelectCountryForm;
